import React, { useState } from "react";
import { useGame } from "@/context/GameContext";
import { Coins, Star, PiggyBank, Calendar, Home, Volume2, VolumeX } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sound } from "@/lib/sound";
import Avatar from "@/components/avatar/Avatar";
import BrandBadge from "@/components/brand/BrandBadge";
import { BUSINESSES } from "@/data/gameData";

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div
    className={`flex items-center gap-2 rounded-2xl bg-white border-b-4 ${color.border} border ${color.borderLite} px-3 py-1.5 min-w-0`}
    data-testid={testid}
  >
    <Icon className={`w-4 h-4 ${color.icon} shrink-0`} />
    <div className="flex flex-col leading-tight min-w-0">
      <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span className={`text-sm font-extrabold ${color.text} truncate`}>{value}</span>
    </div>
  </div>
);

const STAT_COLORS = {
  CASH: { border: "border-b-emerald-500", borderLite: "border-emerald-200", icon: "text-emerald-500", text: "text-emerald-700" },
  PIGGY: { border: "border-b-blue-500", borderLite: "border-blue-200", icon: "text-blue-500", text: "text-blue-700" },
  REP: { border: "border-b-yellow-500", borderLite: "border-yellow-200", icon: "text-yellow-500", text: "text-yellow-700" },
  DAY: { border: "border-b-violet-500", borderLite: "border-violet-200", icon: "text-violet-500", text: "text-violet-700" },
};

const HUD = ({ showHome = true }) => {
  const { state, setAvatarMakerOpen, getBrand } = useGame();
  const navigate = useNavigate();
  const bizId = useLocation().pathname.split("/business/")[1]?.split("?")[0];
  const [prefs, setPrefs] = useState(Sound.getPrefs());

  const toggleSfx = () => {
    Sound.toggleSfx();
    setPrefs(Sound.getPrefs());
    Sound.tap();
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b-4 border-amber-300">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
        {showHome && (
          <button
            onClick={() => { Sound.click(); navigate("/"); }}
            data-testid="hud-home-btn"
            className="btn-3d bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-2xl px-3 py-2 font-black flex items-center gap-1.5 border-2 border-amber-500"
          >
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline text-sm">Hub</span>
          </button>
        )}
        <button
          onClick={() => { Sound.click(); setAvatarMakerOpen(true); }}
          data-testid="hud-avatar-btn"
          title="Edit my look"
          className="rounded-full bg-amber-100 border-2 border-amber-400 w-10 h-10 overflow-hidden flex items-end justify-center btn-3d hover:bg-amber-200"
        >
          <Avatar avatar={state.avatar} size={40} testid="hud-avatar" className="translate-y-1" />
        </button>
        <div className="font-display font-bold text-lg sm:text-xl text-slate-800 mr-1 hidden md:block">
          Kids Biz Boss
        </div>
        {bizId && BUSINESSES[bizId] && (
          <BrandBadge brand={getBrand(bizId)} biz={BUSINESSES[bizId]} size="sm" className="hidden sm:inline-flex max-w-[220px]" testid="hud-brand" />
        )}
        <button
          onClick={toggleSfx}
          data-testid="hud-sfx-toggle"
          title={prefs.sfxOn ? "Mute sound effects" : "Enable sound effects"}
          className={`rounded-xl px-2 py-2 border-2 btn-3d ${prefs.sfxOn ? "bg-emerald-400 hover:bg-emerald-500 border-emerald-500 text-white" : "bg-white hover:bg-slate-50 border-slate-300 text-slate-500"}`}
        >
          {prefs.sfxOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <Stat icon={Coins} label="Cash" value={`$${state.cash}`} color={STAT_COLORS.CASH} testid="hud-cash" />
          <Stat icon={PiggyBank} label="Piggy" value={`$${state.piggyBank}`} color={STAT_COLORS.PIGGY} testid="hud-piggy" />
          <Stat icon={Star} label="Rep" value={`${state.reputation.toFixed(1)}★`} color={STAT_COLORS.REP} testid="hud-rep" />
          <Stat icon={Calendar} label="Day" value={state.day} color={STAT_COLORS.DAY} testid="hud-day" />
        </div>
      </div>
    </header>
  );
};

export default HUD;
